<template ref="form">
    <div class="card-reg">
        <div>
            <span class="p-float-label">
                <Dropdown id="salutation" data-cy="wizard-salutation" v-model="v$.salutation.$model" :options="salutations" :class="{'p-invalid':v$.salutation.$error}" />
                <label class="requiredLabel" for="salutation">{{t("salutation")}} *</label>
            </span>
            <div v-if="showMessageOnError">
                <small class="p-error" v-for="(error, index) of v$.salutation.$errors" :key="index">
                    {{ t(error.$message) }}
                </small>
            </div>
        </div>
        <div>
            <span class="p-float-label">
                <Dropdown id="title" v-model="v$.academicTitle.$model" :options="titles" :class="{'p-invalid':v$.academicTitle.$error}" />
                <label for="title">{{t("academic.title")}}</label>
            </span>
            <div v-if="showMessageOnError">
                <small class="p-error" v-for="(error, index) of v$.academicTitle.$errors" :key="index">
                    {{ t(error.$message) }}
                </small>
            </div>
        </div>
        <div>
            <span class="p-float-label">
                <InputText id="firstName" data-cy="wizard-firstName" v-model="user.firstName" type="text" :class="{'p-invalid':v$.firstName.$error}" />
                <label class="requiredLabel" for="firstname">{{t("profile.form.firstname")}} *</label>
            </span>
            <div v-if="showMessageOnError">
                <small class="p-error" v-for="(error, index) of v$.firstName.$errors" :key="index">
                    {{ t(error.$message) }}
                </small>
            </div>
        </div>
        <div>
            <span class="p-float-label">
                <InputText id="lastName" data-cy="wizard-lastName" v-model="v$.lastName.$model" type="text" :class="{'p-invalid':v$.lastName.$error}" />
                <label class="requiredLabel" for="lastname">{{t("profile.form.lastname")}} *</label>
            </span>
            <div v-if="showMessageOnError">
                <small class="p-error" v-for="(error, index) of v$.lastName.$errors" :key="index">
                    {{ t(error.$message) }}
                </small>
            </div>
        </div>
        <div class="location">
            <span class="p-float-label">
                <AutoComplete v-model="zipAndCity"
                              forceSelection
                              :autoHighlight="true"
                              :suggestions="locations"
                              :inputClass="{'p-invalid':v$.zipCode.$error}"
                              @complete="loadLocations($event)"
                              @itemSelect="locationItemSelected($event)"
                              @clear="user.zipCode=''"
                              id="zipCode"
                              data-cy="wizard-zipCode" />

                <label :class="[{'invalid': v$.zipCode.$error}, 'requiredLabel']" for="zipCode">{{t("place.of.residence.postcode") + (isDirectSignUp ? "" : " *")}}</label>
            </span>
            <div v-if="showMessageOnError">
                <small class="p-error" v-for="(error, index) of v$.zipCode.$errors" :key="index">
                    {{ t(error.$message) }}
                </small>
            </div>
        </div>
        <div class="phone">
            <span class="p-float-label">
                <InputText id="phoneNumber" data-cy="wizard-phoneNumber" v-model="truncatedPhoneNumber" type="tel" :class="{'p-invalid':v$.phoneNumber.$error}" />
                <div class="country-prefix">+49</div>
                <label :class="[{'invalid': v$.zipCode.$error}, 'requiredLabel no-tooltip']" for="phoneNumber">{{t("phone.number") + (isDirectSignUp ? "" : " *")}}</label>
            </span>
            <div v-if="showMessageOnError">
                <small class="p-error" v-for="(error, index) of v$.phoneNumber.$errors" :key="index">
                    {{ t(error.$message) }}
                </small>
            </div>

        </div>
    </div>

    <div class="call-to-action mobile-bottom-nav" v-if="showButtons">
        <Button v-if="isDirectSignUp" data-cy="button-profile-prev"
                class="button-secondary button-back p-pl-3"
                @click="prev()">
            <i class="pi pi-angle-left" />{{t("back")}}
        </Button>
        <div></div>
        <Button v-if="creationOption == 'standalone'" data-cy="button-profile-next" class="button-secondary button-next p-pr-3" @click="next()">
            {{t("continue")}}  <i class="pi pi-angle-right"></i>
        </Button>
        <Button v-else data-cy="button-secondary button-next"
                class="button-rectangle button-secondary"
                :label="t('finish')"
                @click="submit()" />

    </div>

</template>
<script lang="ts" setup>
    import { computed, ref, onMounted, getCurrentInstance, defineEmits, defineProps } from 'vue';
    import useVuelidate from '@vuelidate/core';
    import { required, requiredIf, numeric, helpers, maxLength, minLength } from '@vuelidate/validators';
    import { createCityZipCodeService } from '../services/cityZipCodeService';
    import { createAuthenticationService } from '../services/authenticationService';
    import { useI18n } from 'vue-i18n';

    const emit = defineEmits(['next', 'previous', 'submit']);
    const props = defineProps(["modelValue", "showButtons", "isDirectSignUp"]);

    const { t } = useI18n();

    const form = ref(null);
    const zipAndCity = ref();
    const zipCodes = ref([]);
    const salutations = ref([t("salutations.mr"), t("salutations.mrs")]);

    const creationOption = ref("standalone");
    const titles = ref([null, "Dr.", "Prof.", "Prof. Dr."]);

    const app = getCurrentInstance();
    const authenticationService = createAuthenticationService(app);
    const user = ref({ ...props.modelValue });

    const rules = computed(() => {
        return {
            salutation: { required: helpers.withMessage("Eingabe wird benötigt", required) },
            firstName: { required: helpers.withMessage("Eingabe wird benötigt", required) },
            lastName: { required: helpers.withMessage("Eingabe wird benötigt", required) },
            zipCode: {
                requiredIf: helpers.withMessage(t("Eingabe wird benötigt"), requiredIf(() => {
                    return !props.isDirectSignUp;
                })),
                numeric: helpers.withMessage("validate.numeric.error", numeric),
                minLength: helpers.withMessage("validate.zipcode.error", minLength(5)),
                maxLength: helpers.withMessage("validate.zipcode.error", maxLength(5))
            },
            phoneNumber: {
                requiredIf: helpers.withMessage(t("Eingabe wird benötigt"), requiredIf(() => {
                    return !props.isDirectSignUp;
                })),
            },
            academicTitle: {},
        }
    });

    const truncatedPhoneNumber = computed({
        get: () => {
            if (user.value?.phoneNumber?.indexOf('+49') == 0)
                return user.value.phoneNumber.substring(3).trim();
            return user.value.phoneNumber;
        },
        set: (val) => {
            user.value.phoneNumber = "+49 " + val;
        }
    });

    const fetchCityZipCodes = async function () {
        const service = await createCityZipCodeService(app);
        const response = await service.getAll()
        zipCodes.value = response.data;

        if (user.value.zipCodeIds == undefined || user.value.zipCodeIds == null || user.value.zipCodeIds.length == 0)
            return;
        for (let i = 0; i < user.value.zipCodeIds.length; i++) {
            selectedZipCodes.value.push(await (await service.getById(user.value.zipCodeIds[i])).data);
        }
    }

    const userMail = ref("");

    const showMessageOnError = ref(false);

    onMounted(async () => {
        userMail.value = await authenticationService.getUserEmail();
        await fetchCityZipCodes();
        zipAndCity.value = await requestNameByZip(user.value.zipCode);
    });

    const requestNameByZip = async (zip: string) => {
        const service = await createCityZipCodeService(app);
        if (zip) {
            const result = await service.zip(zip);

            if (result.status == 200) {
                if (result.data) {
                    const data = result.data as any;
                    return data.zip + " " + data.cityName;
                }
            }
        }

        return null;
    };


    const v$ = useVuelidate(rules, user);
    const validate = () => {
        v$.value.$touch();
        if (v$.value.$invalid) {
            return v$.value.$error;
        }
    }

    const selectedZipCodes = ref([]);

    const locations = ref();

    const loadLocations = async (event) => {
        if (event.query.trim().length < 3) {
            locations.value = [];
        }
        else {
            const service = await createCityZipCodeService(app);
            const data = await service.locations(event.query.trim());
            locations.value = data.data;
        }
    };

const locationItemSelected = (event) => {
    if (event.value) {
        user.value.zipCode = event.value?.split(' ')[0];
    }
}

    const next = () => {
        if (!validate()) {
            user.value.zipCodeIds = selectedZipCodes.value.map(x => x.id);
            emit("next", user.value);
        }
        showMessageOnError.value = true;
    };

    const submit = () => {
        if (!validate()) {
            user.value.zipCodeIds = selectedZipCodes.value.map(x => x.id);
            emit("submit", user.value);
        }
        showMessageOnError.value = true;
    };

    const prev = () => {
        emit("previous", user.value);
    };
</script>
